import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import Currency from 'components/Form/Currency';
import NotApplicable from 'components/Form/NotApplicable';
import Text from 'components/Form/Text';
import FailureType from './FailureType';
import { ValidationElementProps } from '../../../Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
} from '../../../../types/props';
import { ErrorCode } from '../../../../redux/modules/application/reducers/error';
import { FinancialTaxesItemModel } from '../../../../forms/sections/financial/taxes/types';

interface Props extends Partial<FinancialTaxesItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class TaxesItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialTaxesItemModel>) => {
    this.props.onUpdate({
      Failure: this.props.Failure,
      Year: this.props.Year,
      YearEstimated: this.props.YearEstimated,
      Reason: this.props.Reason,
      Agency: this.props.Agency,
      TaxType: this.props.TaxType,
      Amount: this.props.Amount,
      AmountEstimated: this.props.AmountEstimated,
      DateNotApplicable: this.props.DateNotApplicable,
      Date: this.props.Date,
      Description: this.props.Description,
      ...queue,
    });
  };

  updateFailure = (values: FinancialTaxesItemModel['Failure']) => {
    this.update({
      Failure: values,
    });
  };

  updateYear = (values: FinancialTaxesItemModel['Year']) => {
    this.update({
      Year: values,
    });
  };

  updateReason = (values: FinancialTaxesItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateAgency = (values: FinancialTaxesItemModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateTaxType = (values: FinancialTaxesItemModel['TaxType']) => {
    this.update({
      TaxType: values,
    });
  };

  updateAmount = (values: FinancialTaxesItemModel['Amount']) => {
    this.update({
      Amount: values,
    });
  };

  updateDateNotApplicable = (values: FinancialTaxesItemModel['DateNotApplicable']) => {
    this.update({
      Date: values.applicable ? this.props.Date : {},
      DateNotApplicable: values,
    });
  };

  updateDate = (values: FinancialTaxesItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDescription = (values: FinancialTaxesItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateYearEstimated = (values: FinancialTaxesItemModel['YearEstimated']) => {
    this.update({
      YearEstimated: values,
    });
  };

  updateAmountEstimated = (values: FinancialTaxesItemModel['AmountEstimated']) => {
    this.update({
      AmountEstimated: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <div className="taxes-item">
        <Field
          title={i18n.t('financial.taxes.heading.failure')}
          adjustFor="buttons"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          attribute={`${attribute}.Failure`}
        >
          <FailureType
            name="Failure"
            {...this.props.Failure}
            className="taxes-failure"
            required={this.props.required}
            onUpdate={this.updateFailure}
            onError={this.props.onError}
            attribute={`${attribute}.Failure`}
          />
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.year')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Year`}
        >
          <DateControl
            name="Year"
            {...this.props.Year}
            className="taxes-year"
            hideMonth
            hideDay
            showEstimated={false}
            minDate={this.props.Year}
            minDateEqualTo
            required={this.props.required}
            onUpdate={this.updateYear}
            onError={this.props.onError}
            attribute={`${attribute}.Year`}
          />
          <div className="flags">
            <Checkbox
              name="YearEstimated"
              label={i18n.t('financial.taxes.label.estimated')}
              toggle="false"
              {...this.props.YearEstimated}
              onUpdate={this.updateYearEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.YearEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            {...this.props.Reason}
            className="taxes-reason"
            required={this.props.required}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            attribute={`${attribute}.Reason`}
          />
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.agency')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            className="taxes-agency"
            required={this.props.required}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            attribute={`${attribute}.Agency`}
          />
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.taxtype')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TaxType`}
        >
          <Text
            name="TaxType"
            {...this.props.TaxType}
            className="taxes-taxtype"
            required={this.props.required}
            onUpdate={this.updateTaxType}
            onError={this.props.onError}
            attribute={`${attribute}.TaxType`}
          />
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.amount')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Amount`}
        >
          <div>
            <Currency
              name="Amount"
              {...this.props.Amount}
              className="taxes-amount"
              min="1"
              required={this.props.required}
              onUpdate={this.updateAmount}
              onError={this.props.onError}
              attribute={`${attribute}.Amount`}
            />
            <div className="flags">
              <Checkbox
                name="AmountEstimated"
                label={i18n.t('financial.taxes.label.estimated')}
                toggle="false"
                {...this.props.AmountEstimated}
                onUpdate={this.updateAmountEstimated}
                onError={this.props.onError}
                attribute={`${attribute}.AmountEstimated`}
              />
            </div>
          </div>
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.date')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          className="taxes-date-notapplicable"
          shrink
          attribute={`${attribute}.Date`}
        >
          <NotApplicable
            name="DateNotApplicable"
            {...this.props.DateNotApplicable}
            label={i18n.t('financial.taxes.label.notapplicable')}
            or={i18n.m('financial.taxes.para.or')}
            onUpdate={this.updateDateNotApplicable}
            onError={this.props.onError}
            attribute={`${attribute}.DateNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Date?.month || this.props.Date?.year)}
          >
            <DateControl
              name="Date"
              {...this.props.Date}
              className="taxes-date"
              hideDay
              minDate={this.props.Year}
              prefix="taxesSatisfied"
              minDateEqualTo
              required={this.props.required}
              onUpdate={this.updateDate}
              onError={this.props.onError}
              attribute={`${attribute}.Date`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.taxes.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            className="taxes-description"
            required={this.props.required}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            attribute={`${attribute}.Description`}
          />
        </Field>
      </div>
    );
  }
}

TaxesItem.defaultProps = {
  Failure: {},
  Year: {},
  YearEstimated: {},
  Reason: {},
  Agency: {},
  TaxType: {},
  Amount: {},
  AmountEstimated: {},
  DateNotApplicable: { applicable: true },
  Date: {},
  Description: {},
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
  required: false,
};
